import React from "react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

const fmtUSD = (n) =>
    `$${Number(n || 0).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;

/**
 * Iter60.p25 — DeductionBreakdown
 *
 * Renders the deductions_total as a small clickable cell. On click, opens a
 * popover with per-entry list: amount + reason string. When the list is
 * empty, renders the total plainly (still clickable so the affordance is
 * consistent, but the popover shows "No deductions").
 *
 * Props:
 *   - lineId: the payroll_line UUID (used for data-testid)
 *   - total: numeric total ($)
 *   - deductions: array of { label, amount, reason, source, note?, notes?, work_date? }
 */
export default function DeductionBreakdown({ lineId, total, deductions }) {
    const items = Array.isArray(deductions) ? deductions : [];
    const empty = items.length === 0;
    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className="mono text-xs text-rose-700 hover:underline focus:outline-none focus:ring-1 focus:ring-rose-400 rounded"
                    data-testid={`deduction-breakdown-trigger-${lineId}`}
                    title={empty ? "No deductions" : "Click for breakdown"}
                >
                    {fmtUSD(total)}
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="end"
                className="w-80"
                data-testid={`deduction-breakdown-${lineId}`}
            >
                <div className="flex items-baseline justify-between border-b pb-2 mb-2">
                    <div className="text-sm font-semibold asl-navy-text">
                        Deductions
                    </div>
                    <div className="mono text-sm text-rose-700 font-bold">
                        {fmtUSD(total)}
                    </div>
                </div>
                {empty ? (
                    <div
                        className="text-xs text-[color:var(--asl-muted)] italic"
                        data-testid={`deduction-breakdown-empty-${lineId}`}
                    >
                        No deductions on this line.
                    </div>
                ) : (
                    <ul
                        className="space-y-1.5 max-h-64 overflow-y-auto"
                        data-testid={`deduction-breakdown-list-${lineId}`}
                    >
                        {items.map((d, i) => {
                            const reason =
                                d.reason ||
                                d.notes ||
                                d.note ||
                                d.label ||
                                "(no reason)";
                            const label = d.label || "Item";
                            const source = d.source || "";
                            const badge =
                                source === "recurring"
                                    ? "recurring"
                                    : source === "bulk_line_item"
                                    ? "manual"
                                    : source === "auto"
                                    ? "auto"
                                    : "";
                            return (
                                <li
                                    key={i}
                                    className="text-xs flex items-start gap-2"
                                    data-testid={`deduction-item-${lineId}-${i}`}
                                >
                                    <span className="mono text-rose-700 font-semibold min-w-[60px] text-right">
                                        {fmtUSD(d.amount)}
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <div className="asl-navy-text truncate">
                                            {reason}
                                        </div>
                                        <div className="text-[10px] text-[color:var(--asl-muted)] flex items-center gap-1.5">
                                            <span>{label}</span>
                                            {badge && (
                                                <span className="px-1 py-[1px] rounded bg-slate-100 text-slate-600 uppercase tracking-wider">
                                                    {badge}
                                                </span>
                                            )}
                                            {d.work_date && (
                                                <span>· {d.work_date}</span>
                                            )}
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </PopoverContent>
        </Popover>
    );
}
